import { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useShallow } from 'zustand/react/shallow';
import {
  BarChart3,
  Blocks,
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Database,
  HelpCircle,
  History,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  Settings,
  Sparkles,
  Users,
  Video,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePreferencesStore } from '@/entities/preferencesStore';
import { config } from '@/shared/config/env';
import { cn } from '@/lib/utils';

/**
 * Sidebar. 1:1 port of the Figma Sidebar.tsx (220px expanded /
 * 56px collapsed, BIASafe wordmark, divider above "Clients", primary
 * + sub-items menu with expand/collapse, sliding active indicator,
 * bottom group for Settings / Docs / Help Desk).
 *
 * Drop-in for our previous SidebarItem-based layout. Motion is plain
 * Tailwind transitions (no motion/react dependency). Items map to
 * our TanStack routes where they exist; sub-items navigate to the
 * parent route until child routes are added.
 */

export type NavItem = {
  icon: LucideIcon;
  label: string;
  id: string;
  href?: string;
  subItems?: Array<{ label: string; id: string; href?: string }>;
  divider?: boolean;
  /**
   * A count to show against the row, when there is something waiting. Zero and
   * undefined both render nothing — a badge reading "0" is a notification that
   * there are no notifications.
   */
  badge?: number;
};

const MAIN_ITEMS: Array<NavItem> = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    id: 'Dashboard',
    href: '/dashboard',
  },
  { icon: Sparkles, label: 'Soteria AI', id: 'soteria', href: '/soteria' },
  // Clients opens the "client + accounts" section; the divider marks the start
  // of the operational group.
  {
    icon: Users,
    label: 'Clients',
    id: 'clients',
    href: '/clients',
    divider: true,
  },
  {
    // Sub-items (Holdings / Activity / Performance) moved to the page's top
    // tab bar — the sidebar just links to the section.
    icon: Briefcase,
    label: 'Portfolios',
    id: 'livePortfolio2',
    href: '/portfolio',
  },
  {
    // Sub-items (Markets / Rebalancer / Orders / Trades) moved to the page's
    // top tab bar — the sidebar just links to the section.
    icon: BarChart3,
    label: 'Execution',
    id: 'Execution',
    href: '/execution',
  },
  {
    // Meeting notetaker bots. Sits under Execution because an advisor reaches
    // it from the same "what am I doing today" context as orders, not from
    // Settings.
    icon: Video,
    label: 'Meeting bots',
    id: 'MeetingBots',
    href: '/meeting-bots',
  },
  {
    // No sub-items: the Model Engine / Marketplace / Benchmarks tabs now live
    // in the page's top tab bar (BacktestMainTabs), so the sidebar just links
    // straight to the Strategies hub.
    icon: Database,
    label: 'Strategies',
    id: 'Strategies',
    href: '/strategies',
  },
  // Administration moved into Settings (admin-only top-tab); no longer a
  // top-level sidebar entry.
];

const BOTTOM_ITEMS: Array<NavItem> = [
  {
    icon: Blocks,
    label: 'Integrations',
    id: 'integrations',
    href: '/integrations',
  },
  // Opening an account starts FROM A CLIENT, so it starts on the client's row
  // and on their page. It was here and in the header as well, which put the
  // same task in three places and this one directly above Settings, where it
  // read as configuration rather than as the job.
  //
  // `/clients/open-account` still exists and still begins at client selection:
  // the Client Assistant hands off to it, and so does the tasks widget.
  {
    icon: Settings,
    label: 'Settings',
    id: 'settings',
    href: '/settings',
  },
  {
    icon: BookOpen,
    label: 'Documentation',
    id: 'docs',
    href: '/docs',
    divider: true,
  },
  { icon: History, label: 'Changelog', id: 'changelog', href: '/changelog' },
  { icon: HelpCircle, label: 'Help Desk', id: 'help', href: '/help' },
];

/**
 * The nav a Sidebar renders. Defaults to the advisor product's.
 *
 * Parameterised rather than duplicated so the client portal gets the SAME
 * component — the same widths, the same collapse behaviour and preference, the
 * same active indicator, the same hover states. A second sidebar built to
 * match this one would start identical and drift on the first change to
 * either, and the two are seen by different people, so nobody would notice.
 */
export interface SidebarProps {
  mainItems?: Array<NavItem>;
  bottomItems?: Array<NavItem>;
  /**
   * Section roots that stay highlighted for their sub-routes. The advisor
   * default carries its own special cases below; a caller passing nav of its
   * own passes the prefixes that behave that way in it.
   */
  sectionRoots?: Array<string>;
}

export const Sidebar = ({
  mainItems = MAIN_ITEMS,
  bottomItems = BOTTOM_ITEMS,
  sectionRoots,
}: SidebarProps = {}) => {
  const { isCollapsed, toggleSidebar } = usePreferencesStore(
    useShallow((state) => ({
      isCollapsed: state.isCollapsed,
      toggleSidebar: state.toggleSidebar,
    })),
  );
  // THE DRAWER NO LONGER NARROWS IT, and the reason is a bug this caused.
  //
  // It read `!isCollapsed && !isSoteriaOpen`, to match `AuthLayout`'s own
  // `effectiveCollapsed` "so the main content margin stays in sync". The same
  // rule in two files, agreeing by hand. When the layout's copy was removed,
  // this one stayed: the bar drew itself at 56px while the layout still
  // reserved 220px for it, and the page opened with a 164px strip of nothing
  // down its left edge.
  //
  // One source now. The bar is as wide as the preference says, and the layout
  // reads the same preference — there is nothing left to keep in step.
  const isExpanded = !isCollapsed;
  const location = useLocation();
  const currentPath = location.pathname;
  const [expandedItems, setExpandedItems] = useState<Array<string>>([]);

  // Account opening lives under /clients but is its own nav entry, so it must
  // not also light up the Clients section root — two highlighted rows read as a
  // broken indicator rather than a nested one.
  const isOpeningAnAccount = currentPath.includes('/open-account');

  const isActive = (item: NavItem) => {
    if (!item.href) return false;
    // A caller supplying its own nav supplies its own section roots; without
    // this the advisor special cases below would be the only prefix matching
    // any nav, and a portal sub-route would light nothing.
    if (sectionRoots) {
      return sectionRoots.includes(item.href)
        ? currentPath.startsWith(item.href)
        : currentPath === item.href;
    }
    // Section roots stay highlighted for their sub-routes (same as the ETF
    // designer keeps Strategies active).
    if (item.href === '/strategies')
      return currentPath.startsWith('/strategies');
    if (item.href === '/clients')
      return currentPath.startsWith('/clients') && !isOpeningAnAccount;
    if (item.href === '/clients/open-account') return isOpeningAnAccount;
    return currentPath === item.href;
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  return (
    <aside
      data-figma-type
      className={cn(
        'fixed left-0 top-0 h-screen bg-card border-r border-border/50 z-30 flex flex-col',
        'transition-[width] duration-300 ease-out',
        isExpanded ? 'w-[220px]' : 'w-[56px]',
      )}
    >
      {/* Header: wordmark + collapse button */}
      <div
        className={cn(
          'h-14 border-b border-border/50 flex items-center px-3 flex-shrink-0',
          isExpanded ? 'justify-between' : 'justify-center',
        )}
      >
        {/* The wordmark is a fixed PNG, so it cannot follow the theme the way a
            class can. Its glyph is bright purple and reads fine on a dark
            page; "BIASafe" beside it is near-black purple and disappears
            against --background (#0f0b1f).

            `invert` alone is wrong here — it would swing the purple glyph to
            green — and `brightness-0 invert` flattens the gradient to a white
            silhouette. Inverting AND rotating the hue a half turn flips
            lightness while putting the hue back where it started, so the dark
            ink comes up light and both halves stay purple.

            This is a stand-in for a light-variant asset, which is a brand
            deliverable rather than something to approximate in CSS. Drop the
            filter and switch the src when that exists. Light mode is
            untouched. */}
        {isExpanded && (
          <img
            src={`${config.app.basePath}biasafe-logo.png`}
            alt="BIASafe"
            className="h-7 w-auto ml-3 transition-opacity duration-200 dark:invert dark:hue-rotate-180"
          />
        )}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className={cn(
            'h-9 w-9 p-0 rounded-md flex items-center justify-center',
            'hover:bg-primary/10 hover:text-primary transition-colors',
          )}
        >
          {isExpanded ? (
            <PanelLeftClose className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Main menu items */}
      <nav className="flex-1 overflow-y-auto py-8">
        <div className="space-y-1.5 px-3">
          {mainItems.map((item) => (
            <SidebarRow
              key={item.id}
              item={item}
              isExpanded={isExpanded}
              active={isActive(item)}
              expanded={expandedItems.includes(item.id)}
              onToggleExpand={() => toggleExpanded(item.id)}
            />
          ))}
        </div>
      </nav>

      {/* Bottom menu items.
          Rendered only when there ARE any: the wrapper carries a top border,
          so an empty list left a horizontal rule floating above nothing at the
          foot of the sidebar. The portal passes no bottom items — a client has
          no Settings, Documentation or Help Desk. */}
      {bottomItems.length > 0 && (
        <div className="border-t border-border/50 py-4 px-3 flex-shrink-0">
          <div className="space-y-1.5">
            {bottomItems.map((item) => (
              <SidebarRow
                key={item.id}
                item={item}
                isExpanded={isExpanded}
                active={isActive(item)}
                expanded={false}
                onToggleExpand={() => {}}
              />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

function SidebarRow({
  item,
  isExpanded,
  active,
  expanded,
  onToggleExpand,
}: {
  item: NavItem;
  isExpanded: boolean;
  active: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const Icon = item.icon;
  const hasSubItems = !!item.subItems?.length;

  const rowClasses = cn(
    'w-full flex items-center h-10 rounded-lg transition-all duration-200 group relative',
    isExpanded ? 'justify-start px-3' : 'justify-center px-0',
    active
      ? 'text-primary bg-primary/10'
      : // `text-foreground`, NOT `text-muted-foreground`. Muted is the colour of
        // something switched off, and every destination in the product wore it —
        // the nav read as uniformly unavailable, with the one active row the only
        // thing that looked clickable. Set on the ROW so the icon and its label
        // are the same colour by construction rather than by two rules agreeing.
        //
        // The token rather than `#121212`: this file is under the themeTokens
        // ratchet, and a literal here is invisible in light mode and unreadable
        // on a dark sidebar. `--foreground` IS that black in light mode, so the
        // decision above is preserved exactly and now survives the theme flip.
        'text-foreground hover:bg-accent',
  );

  // Bigger, lighter, and black at rest.
  //
  // These read as poorly drawn rather than poorly coloured, and the cause was
  // the stroke: 2.5 on an 18px glyph, thickening to 2.5 again on hover. At that
  // ratio the strokes of a 24px-grid icon nearly meet and the interior detail
  // fills in — the counters in the Clients heads, the bars in Execution. They
  // are inline SVG, so nothing here is a resolution problem; a heavy line on a
  // small canvas is.
  //
  // 20px at 1.75 leaves the interior open. Colour is inherited from the row, so
  // an icon can never disagree with the label beside it.
  const iconClasses =
    'h-5 w-5 flex-shrink-0 transition-all duration-200 stroke-[1.75]';

  const labelClasses = cn(
    'ml-3 text-sm flex-1 text-left',
    active ? 'font-medium' : 'font-normal',
  );

  // Sliding active indicator on the left edge.
  const ActiveIndicator = active ? (
    <span className="absolute left-0 w-0.5 h-6 bg-primary rounded-full" />
  ) : null;

  return (
    <div>
      {item.divider && <div className="h-px bg-border/50 my-3 mx-2" />}
      {hasSubItems && isExpanded ? (
        <>
          <button type="button" onClick={onToggleExpand} className={rowClasses}>
            {ActiveIndicator}
            <Icon className={iconClasses} />
            <span className={labelClasses}>{item.label}</span>
            {expanded ? (
              <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
            ) : (
              <ChevronRight className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
            )}
          </button>
          {expanded && (
            <div className="flex flex-col space-y-1 pl-7 py-1 overflow-hidden">
              {item.subItems!.map((sub) => (
                <Link
                  key={sub.id}
                  to={sub.href ?? item.href ?? '/'}
                  // A step lighter than its parent, deliberately — these are
                  // subordinate entries and should read that way. But an alpha on
                  // `--foreground` rather than `text-muted-foreground`, which is
                  // the same looks-disabled grey the top-level rows just lost.
                  // `/80` is the token spelling of the `#3e3e3e` this carried:
                  // a step down from the parent that still reads as available,
                  // and it steps down from WHITE in dark mode instead of staying
                  // a fixed near-black nobody could read.
                  className="relative flex items-center w-full h-8 px-3 text-sm rounded-md text-foreground/80 hover:text-foreground hover:bg-accent/50 transition-all duration-200"
                >
                  <span className="truncate">{sub.label}</span>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <Link
          to={item.href ?? '/'}
          className={rowClasses}
          aria-label={isExpanded ? undefined : item.label}
        >
          {ActiveIndicator}
          <Icon className={iconClasses} />
          {isExpanded && <span className={labelClasses}>{item.label}</span>}
          {!!item.badge && (
            <span
              className={cn(
                'inline-flex items-center justify-center rounded-pill bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground',
                // Collapsed, the row is 56px of icon with no room beside it,
                // so the count becomes a dot pinned to the icon's corner.
                isExpanded
                  ? 'ml-2 h-5 min-w-5'
                  : 'absolute right-2 top-2 h-2 w-2 px-0',
              )}
            >
              {isExpanded ? (item.badge > 9 ? '9+' : item.badge) : ''}
            </span>
          )}
        </Link>
      )}
    </div>
  );
}
